/**
 * Employees API Client
 *
 * API functions for employee CRUD operations.
 */

import { apiRequest } from './api';

/**
 * Niveau type
 */
export interface Niveau {
  id: number;
  name: string;
  beschrijving?: string;
  criteria: string;
}

/**
 * Employee list item
 */
export interface EmployeeListItem {
  id: number;
  userId: number;
  email: string;
  name: string;
  role: string;
  niveau: { id: number; name: string };
  functie: string | null;
  team: string | null;
  startdatum: string | null;
  active: boolean;
  // Epic 5 (Story 5.1, AD-7): null zolang de medewerker niet uit dienst is.
  uitDienstDatum: string | null;
  tasksCount: number;
  createdAt: string;
}

/**
 * Employee detail (includes tasks and manager)
 */
export interface EmployeeDetail extends EmployeeListItem {
  manager: { id: number; name: string } | null;
  tasks: Array<{ id: number; name: string; weging: number }>;
  updatedAt: string;
  // Epic 5 (Story 5.2, AC3): of de detailpagina de terugdraai-link mag tonen
  // -- server-autoritatief (AD-2), altijd `false` zolang `uitDienstDatum`
  // leeg is.
  canRevertUitDienst: boolean;
}

/**
 * Employee list response
 */
export interface EmployeeListResponse {
  data: EmployeeListItem[];
  total: number;
}

/**
 * Employee list filters
 */
export interface EmployeeFilters {
  search?: string;
  niveauId?: number;
  team?: string;
  includeInactive?: boolean;
}

/**
 * Create employee input
 */
export interface CreateEmployeeInput {
  userId: number;
  niveauId: number;
  functie?: string;
  team?: string;
  startdatum?: string;
}

/**
 * Update employee input
 * Note: niveauId is not included - it's automatically calculated based on assigned tasks
 */
export interface UpdateEmployeeInput {
  functie?: string | null;
  team?: string | null;
  startdatum?: string | null;
}

/**
 * Print competency
 */
export interface PrintCompetency {
  id: number;
  name: string;
  beschrijving: string | null;
  examples: string | null;
  type: 'basis' | 'vak';
  voldoendeCriterium: string | null;
}

/**
 * Print task with competencies
 */
export interface PrintTask {
  id: number;
  name: string;
  beschrijving: string | null;
  voldoendeCriterium: string | null; // Score level 3 criterion for task
  examples: string | null; // Score level 3 examples for task
  weging: number;
  competencies: PrintCompetency[];
  /**
   * Of de taak nog actief is. Een gearchiveerde taak blijft mét haar
   * competenties op het geprinte profiel staan, maar telt niet mee in de
   * samenvatting eronder.
   */
  isActief: boolean;
}

/**
 * Employee print data for PDF/print export
 */
export interface EmployeePrintData {
  employee: {
    id: number;
    name: string;
    email: string;
    functie: string | null;
    team: string | null;
    startdatum: string | null;
    niveau: {
      id: number;
      name: string;
    };
    totalPunten: number;
  };
  tasks: PrintTask[];
  basisCompetencies: PrintCompetency[];
  summary: {
    aantalTaken: number;
    totaalPunten: number;
    aantalVakCompetenties: number;
    aantalBasisCompetenties: number;
    niveau: string;
  };
  generatedAt: string;
}

/**
 * Employees API functions
 */
export const employeesApi = {
  /**
   * List all employees with optional filters
   */
  async list(filters?: EmployeeFilters): Promise<EmployeeListResponse> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.niveauId) params.append('niveauId', String(filters.niveauId));
    if (filters?.team) params.append('team', filters.team);
    if (filters?.includeInactive) params.append('includeInactive', 'true');

    const query = params.toString();
    return apiRequest<EmployeeListResponse>(`/employees${query ? `?${query}` : ''}`);
  },

  /**
   * Get employee by ID
   */
  async get(id: number): Promise<EmployeeDetail> {
    return apiRequest<EmployeeDetail>(`/employees/${id}`);
  },

  /**
   * Create new employee profile
   */
  async create(input: CreateEmployeeInput): Promise<EmployeeDetail> {
    return apiRequest<EmployeeDetail>('/employees', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update employee profile
   */
  async update(id: number, input: UpdateEmployeeInput): Promise<EmployeeDetail> {
    return apiRequest<EmployeeDetail>(`/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete employee profile
   */
  async delete(id: number): Promise<void> {
    return apiRequest<void>(`/employees/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get unique teams
   */
  async getTeams(): Promise<string[]> {
    return apiRequest<string[]>('/employees/teams');
  },

  /**
   * Get employee print data for PDF/print export
   */
  async getPrintData(id: number): Promise<EmployeePrintData> {
    return apiRequest<EmployeePrintData>(`/employees/${id}/print`);
  },
};

/**
 * Role type
 */
export type Role = 'admin' | 'hr' | 'beoordelaar' | 'medewerker';

/**
 * Role update input
 */
export interface RoleUpdateInput {
  role: Role;
  managerId?: number | null;
}

/**
 * Roles API functions
 */
export const rolesApi = {
  /**
   * Update user role
   */
  async updateRole(userId: number, input: RoleUpdateInput): Promise<void> {
    await apiRequest<void>(`/roles/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
};

/**
 * Niveaus API functions
 */
export const niveausApi = {
  /**
   * List all niveaus
   */
  async list(): Promise<Niveau[]> {
    return apiRequest<Niveau[]>('/niveaus');
  },

  /**
   * Get niveau by ID
   */
  async get(id: number): Promise<Niveau> {
    return apiRequest<Niveau>(`/niveaus/${id}`);
  },
};

/**
 * Niveau override status for a single employee
 *
 * Epic 1 — Roltypes & niveau-bepaling v1, Story 1.3 (AC1, AC2).
 * Mirrors `getEmployeeNiveauStatus`'s return shape in
 * `packages/backend/src/services/niveauService.ts`.
 */
export interface NiveauOverrideStatus {
  employeeId: number;
  currentNiveauId: number;
  currentNiveau: {
    id: number;
    name: string;
    usesMarktcorrectie: boolean;
    marktcorrectiePercentage: number;
  };
  berekendNiveauId: number | null;
  berekendNiveau: { id: number; name: string } | null;
  totaalPunten: number;
  niveauOverride: boolean;
  overrideReden: string | null;
  assignedTasks: Array<{ id: number; name: string; weging: number }>;
}

/**
 * Set niveau override input
 */
export interface SetNiveauOverrideInput {
  niveauId: number;
  reden: string;
}

/**
 * Set niveau override result
 */
export interface NiveauOverrideResult {
  employeeId: number;
  previousNiveauId: number | null;
  newNiveauId: number;
  berekendNiveauId: number | null;
  totaalPunten: number;
  niveauOverride: boolean;
  overrideReden: string | null;
}

/**
 * Niveau Override API functions (Story 1.3)
 */
export const niveauOverrideApi = {
  /**
   * Get niveau status (current, calculated, override info) for an employee
   */
  async getNiveauStatus(employeeId: number): Promise<NiveauOverrideStatus> {
    return apiRequest<NiveauOverrideStatus>(`/employees/${employeeId}/niveau-status`);
  },

  /**
   * Set a manual niveau override for an employee (with mandatory reden)
   */
  async setNiveauOverride(employeeId: number, input: SetNiveauOverrideInput): Promise<NiveauOverrideResult> {
    return apiRequest<NiveauOverrideResult>(`/employees/${employeeId}/niveau-override`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Remove the niveau override for an employee (reverts to calculated niveau)
   */
  async removeNiveauOverride(employeeId: number): Promise<NiveauOverrideResult> {
    return apiRequest<NiveauOverrideResult>(`/employees/${employeeId}/niveau-override`, {
      method: 'DELETE',
    });
  },
};

/**
 * Set uit-dienst input (Story 5.1/5.2, AD-7)
 */
export interface SetUitDienstInput {
  uitDienstDatum: string;
}

/**
 * Uit-dienst API functions (Epic 5, Story 5.1 backend / Story 5.2 UI)
 *
 * Eigen object naast `employeesApi` -- zelfde precedent als
 * `niveauOverrideApi` hierboven (een klein, groeiend sub-domein op
 * `/employees/:id/...`, geen uitbreiding van de generieke CRUD-functies).
 */
export const uitDienstApi = {
  /**
   * Register an employee's uit-dienst-datum. Closes the open assignment
   * period and marks the Nmbrs link inactive (server-side, AD-7).
   */
  async setUitDienst(employeeId: number, input: SetUitDienstInput): Promise<EmployeeDetail> {
    return apiRequest<EmployeeDetail>(`/employees/${employeeId}/uit-dienst`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Revert a previously registered uit-dienst-datum. Rejects with
   * `ApiRequestError` (`code: 'EVALUATION_ROUND_EXISTS'`) when a later
   * evaluation round already exists.
   */
  async revertUitDienst(employeeId: number): Promise<EmployeeDetail> {
    return apiRequest<EmployeeDetail>(`/employees/${employeeId}/uit-dienst`, {
      method: 'DELETE',
    });
  },
};
